import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  TablePagination,
  Select,
  MenuItem as SelectMenuItem,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MedicalServices as MedicationIcon,
} from '@mui/icons-material';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { useForm, Controller } from 'react-hook-form';

import {
  medicationService,
  Medication,
  MedicationCreate,
} from '../services/medicationService';
import { MEDICATION_UNIT_OPTIONS } from '../constants/medicationUnits';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import RequirePermission from '../components/common/RequirePermission';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { medicationColumns } from '../config/medicationColumns';

const MedicationsPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [medicationToDelete, setMedicationToDelete] = useState<Medication | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { register, handleSubmit, formState: { errors }, reset, setValue, control } = useForm<MedicationCreate>();

  const getSortValue = useCallback((medication: Medication, key: string): any => {
    switch (key) {
      case 'name': return medication.name.toLowerCase();
      case 'category': return (medication.category || '').toLowerCase();
      case 'sub_category': return (medication.sub_category || '').toLowerCase();
      case 'duration_in_days': return medication.duration_in_days ?? 0;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((medication: Medication): string[] => {
    return [
      medication.name,
      medication.category || '',
      medication.sub_category || '',
    ];
  }, []);

  const table = useTablePage<Medication>({
    entityType: 'medications',
    columns: medicationColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const loadMedications = useCallback(async () => {
    if (!currentTenant) return;
    table.loadData(() => medicationService.getMedications(currentTenant.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant]);

  useEffect(() => {
    if (currentTenant) {
      loadMedications();
    }
  }, [currentTenant, loadMedications]);

  const handleOpenDialog = (medication?: Medication) => {
    if (medication) {
      setEditingMedication(medication);
      setValue('name', medication.name);
      setValue('dosage_amount', medication.dosage_amount ?? undefined);
      setValue('dosage_unit', medication.dosage_unit ?? undefined);
      setValue('freq_amount', medication.freq_amount ?? undefined);
      setValue('freq_period', medication.freq_period ?? undefined);
      setValue('duration_in_days', medication.duration_in_days ?? undefined);
      setValue('category', medication.category ?? undefined);
      setValue('sub_category', medication.sub_category ?? undefined);
    } else {
      setEditingMedication(null);
      reset();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMedication(null);
    reset();
  };

  const crud = useCrudHandlers<Medication>({
    table,
    createFn: (data) => medicationService.createMedication({ ...data, tenant_id: currentTenant!.id }),
    updateFn: (id, data) => medicationService.updateMedication(id as number, data),
    deleteFn: (id) => medicationService.deleteMedication(id as number),
    entityName: 'Medication',
    onSuccess: () => { handleCloseDialog(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setMedicationToDelete(null); },
    reloadData: loadMedications,
  });

  const onSubmit = (data: MedicationCreate) => {
    crud.handleCreateOrUpdate(data, editingMedication);
  };

  const handleDelete = () => {
    crud.handleDelete(medicationToDelete);
  };

  const getColumnValue = (medication: Medication, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Box display="flex" alignItems="center">
            <MedicationIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="medium">{medication.name}</Typography>
          </Box>
        );
      case 'category':
        return <Typography variant="body2">{medication.category || '—'}</Typography>;
      case 'sub_category':
        return <Typography variant="body2">{medication.sub_category || '—'}</Typography>;
      case 'dosage':
        return medication.dosage_amount != null
          ? <Typography variant="body2">{medication.dosage_amount} {medication.dosage_unit || ''}</Typography>
          : <Typography variant="body2" color="text.secondary">—</Typography>;
      case 'frequency':
        return medication.freq_amount != null
          ? <Typography variant="body2">{medication.freq_amount}x / {medication.freq_period || ''}</Typography>
          : <Typography variant="body2" color="text.secondary">—</Typography>;
      case 'duration_in_days':
        return medication.duration_in_days != null
          ? <Typography variant="body2">{medication.duration_in_days}</Typography>
          : <Typography variant="body2" color="text.secondary">—</Typography>;
      default: return '';
    }
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: () => { handleOpenDialog(table.selectedItem!); table.handleMenuClose(); } },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: () => { setMedicationToDelete(table.selectedItem!); setDeleteDialogOpen(true); table.handleMenuClose(); }, color: 'error' as const },
    ];
  };

  const renderMobileCard = (medication: Medication) => (
    <Box sx={{ pr: 5 }}>
      <Box display="flex" alignItems="center" mb={0.5}>
        <MedicationIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{medication.name}</Typography>
      </Box>
      {(medication.category || medication.sub_category) && (
        <Typography variant="body2" color="text.secondary">
          {[medication.category, medication.sub_category].filter(Boolean).join(' › ')}
        </Typography>
      )}
      {medication.dosage_amount != null && (
        <Typography variant="body2" color="text.secondary">
          {medication.dosage_amount} {medication.dosage_unit}
          {medication.freq_amount != null && ` · ${medication.freq_amount}x/${medication.freq_period}`}
          {medication.duration_in_days != null && ` · ${medication.duration_in_days}d`}
        </Typography>
      )}
    </Box>
  );

  // Reset to first page when search/sort changes
  const totalCount = table.sortedData.length;
  const paginatedData = table.sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <RequirePermission check={!!user?.user_privilege?.config} featureName="medications">
      <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading medications:">
        <DataTableToolbar
          {...table.toolbarProps}
          title="Medications"
          searchPlaceholder="Search medications by name..."
          actions={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add Medication
            </Button>
          }
        />

        {isMobile ? (
          <MobileCardGrid
            data={paginatedData}
            renderCard={renderMobileCard}
            getItemKey={(m) => m.id}
            onMenuClick={table.handleRowClick}
            emptyIcon={<MedicationIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
            emptyMessage="No medications found"
          />
        ) : (
          <DataTableContainer {...table.containerProps}>
            <DataTableBody
              data={paginatedData}
              {...table.tableBodyProps}
              getColumnValue={getColumnValue}
              getRowKey={(m) => m.id}
              onRowClick={table.handleRowClick}
            />
          </DataTableContainer>
        )}

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />

        <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

        {/* Create/Edit Dialog */}
        <ClinicDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title={editingMedication ? 'Edit Medication' : 'Add Medication'}
          onSubmit={handleSubmit(onSubmit)}
          actions={
            <>
              <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
                {editingMedication ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleCloseDialog} sx={CLINIC_DIALOG_SX.cancelButton}>
                Cancel
              </Button>
            </>
          }
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>

            {/* Name — full width */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, gridColumn: '1 / -1' }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name *</Typography>
              <TextField
                {...register('name', { required: 'Name is required' })}
                fullWidth size="small"
                error={!!errors.name}
                helperText={errors.name?.message}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Category */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Category</Typography>
              <TextField
                {...register('category')}
                fullWidth size="small"
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Sub-Category */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Sub-Category</Typography>
              <TextField
                {...register('sub_category')}
                fullWidth size="small"
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Dosage Amount */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Dosage Amount</Typography>
              <TextField
                {...register('dosage_amount', { valueAsNumber: true })}
                fullWidth size="small" type="number"
                inputProps={{ step: 'any', min: 0 }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Dosage Unit */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Dosage Unit</Typography>
              <Controller name="dosage_unit" control={control} defaultValue=""
                render={({ field }) => (
                  <Select {...field} value={field.value ?? ''} fullWidth displayEmpty size="small" sx={CLINIC_DIALOG_SX.select}>
                    <SelectMenuItem value="">— Select —</SelectMenuItem>
                    {MEDICATION_UNIT_OPTIONS.map(({ id, label }) => (
                      <SelectMenuItem key={id} value={label}>{label}</SelectMenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>

            {/* Frequency Amount */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Frequency Amount</Typography>
              <TextField
                {...register('freq_amount', { valueAsNumber: true })}
                fullWidth size="small" type="number"
                inputProps={{ step: 'any', min: 0 }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Frequency Period */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Frequency Period</Typography>
              <Controller name="freq_period" control={control} defaultValue=""
                render={({ field }) => (
                  <Select {...field} value={field.value ?? ''} fullWidth displayEmpty size="small" sx={CLINIC_DIALOG_SX.select}>
                    <SelectMenuItem value="">— Select —</SelectMenuItem>
                    <SelectMenuItem value="Day">Day</SelectMenuItem>
                    <SelectMenuItem value="Week">Week</SelectMenuItem>
                    <SelectMenuItem value="Month">Month</SelectMenuItem>
                  </Select>
                )}
              />
            </Box>

            {/* Duration in Days */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Duration (days)</Typography>
              <TextField
                {...register('duration_in_days', { valueAsNumber: true })}
                fullWidth size="small" type="number"
                inputProps={{ step: 1, min: 0 }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

          </Box>
        </ClinicDialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDelete}
          entityType="Medication"
          entityName={medicationToDelete?.name}
        />

        <ManageColumnsModal {...table.manageColumnsProps} />
        <SaveViewModal {...table.saveViewModalProps} />
      </TablePageLayout>
    </RequirePermission>
  );
};

export default MedicationsPage;
